/**
 * Scrollable timeline grid aligned with {@link TimelineHeader}: for each schedule
 * row it renders day cells (droppable targets for ScopeBar moves) and absolutely positioned
 * ScopeBars (drag + edge resize). Handles pointer-driven resize preview/commit; empty
 * in-range cells open a context menu (Create Scope / Paste Scope) on right-click.
 * Shows a “today” column marker when in range.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  SCHEDULE_DEFAULT_CELL_WIDTH_PX,
  SCHEDULE_ROW_HEIGHT,
} from "../constants";
import { getResizeSessionOrigins } from "../model/scheduleBarModel";
import { sameScheduleBlockId } from "../model/scheduleIds";
import {
  getShiftedDate,
  toLocalCalendarDay,
  toLocalDateKey,
} from "../../../../utils/datetime/core";
import { isDayWithinJobActiveRange } from "../model/scheduleJobDateRange";
import {
  getScheduleGridCellBgClass,
  getScheduleRowCellBorderClass,
} from "../model/scheduleRowStriping";
import { isScheduleWeekGapDivider } from "../model/scheduleTimelineWeekBoundary";
import { CascadeDropDown } from "../../../../components/ui";
import ScopeBar from "./ScopeBar";
import TimelineGridCell from "./TimelineGridCell";
import { useScheduleContext } from "../../../../context/domain/planning/ScheduleContext";
import { BG_SURFACE } from "../style";

/**
 * Map pointer movement during bar resize to new inclusive start/end dates.
 *
 * @param {Object} d Resize session payload.
 * @param {'start'|'end'} d.edge Which edge is being dragged.
 * @param {number} d.startClientX Pointer X when resize began.
 * @param {Date} d.origStart Original inclusive start (local calendar day).
 * @param {Date} d.origEnd Original inclusive end (local calendar day).
 * @param {Date[]} [d.visibleDays] Visible columns (weekend-hidden layouts).
 * @param {number} [d.originStartIndex] Column index of `origStart` in `visibleDays`.
 * @param {number} [d.originEndIndex] Column index of `origEnd` in `visibleDays`.
 * @param {number} clientX Current pointer X.
 * @param {number} cellWidth Day column width in pixels.
 * @returns {{ newStart: Date, newEnd: Date }}
 */
function datesFromDragSession(d, clientX, cellWidth) {
  const deltaColumns = Math.round((clientX - d.startClientX) / cellWidth);
  let newStart = toLocalCalendarDay(d.origStart);
  let newEnd = toLocalCalendarDay(d.origEnd);
  const visibleDays = d.visibleDays ?? [];
  const originIndex = d.edge === "start" ? d.originStartIndex : d.originEndIndex;
  if (visibleDays.length > 0 && originIndex != null && originIndex >= 0) {
    const targetIndex = Math.max(
      0,
      Math.min(visibleDays.length - 1, originIndex + deltaColumns)
    );
    const targetDay = toLocalCalendarDay(visibleDays[targetIndex]);
    if (d.edge === "start") {
      newStart =
        targetDay.getTime() > newEnd.getTime()
          ? toLocalCalendarDay(newEnd)
          : targetDay;
    } else {
      newEnd =
        targetDay.getTime() < newStart.getTime()
          ? toLocalCalendarDay(newStart)
          : targetDay;
    }
    return { newStart, newEnd };
  }
  if (d.edge === "start") {
    newStart = toLocalCalendarDay(getShiftedDate(d.origStart, deltaColumns));
    if (newStart.getTime() > newEnd.getTime()) {
      newStart = toLocalCalendarDay(newEnd);
    }
  } else {
    newEnd = toLocalCalendarDay(getShiftedDate(d.origEnd, deltaColumns));
    if (newEnd.getTime() < newStart.getTime()) {
      newEnd = toLocalCalendarDay(newStart);
    }
  }
  return { newStart, newEnd };
}

/**
 * Resolve a block edge date to a visible column index (weekend-hidden layouts).
 *
 * @param {Date[]} visibleDays Rendered timeline columns.
 * @param {Date} edgeDate Block start or end date.
 * @param {'start'|'end'} edge Which edge is being resolved.
 * @returns {number} Column index, or `-1` when no visible column matches.
 */
function findVisibleEdgeIndex(visibleDays, edgeDate, edge) {
  const edgeKey = toLocalDateKey(edgeDate);
  const exact = visibleDays.findIndex((day) => toLocalDateKey(day) === edgeKey);
  if (exact >= 0) {
    return exact;
  }
  if (edge === "start") {
    return visibleDays.findIndex((day) => toLocalDateKey(day) > edgeKey);
  }
  for (let i = visibleDays.length - 1; i >= 0; i -= 1) {
    if (toLocalDateKey(visibleDays[i]) < edgeKey) {
      return i;
    }
  }
  return -1;
}

/**
 * Schedule timeline grid. Reads domain data/actions from ScheduleContext and accepts
 * only scroll plumbing from the shell that owns the paired left/right scroll containers.
 *
 * @param {Object} props
 * @param {number} [props.horizontalScrollLeft]
 * @param {import('react').RefObject} props.scrollContainerRef
 * @param {Function} [props.onScroll]
 * @returns {JSX.Element}
 */
export default function TimelineCanvas({
  horizontalScrollLeft = 0,
  scrollContainerRef,
  onScroll,
}) {
  const {
    timelineLayout,
    scheduleRows = [],
    rowHeightsById,
    totalGridHeight,
    visibleJobs = [],
    scopeBars = [],
    blocks = [],
    workScopes = [],
    workScopeAspects = [],
    handleCellClick,
    clipboardRevision = 0,
    pasteScopeBlockAtCell,
    handleResizePreview,
    handleResizeCommit,
    isScheduleDayLocked,
  } = useScheduleContext();
  const {
    cellWidth = SCHEDULE_DEFAULT_CELL_WIDTH_PX,
    timelineWidth = 0,
    visibleDays = [],
  } = timelineLayout || {};

  const dragRef = useRef(null);

  const emptyCellContextMenuKeyRef = useRef(null);
  const emptyCellContextOpenRef = useRef(false);
  const [emptyCellContextOpen, setEmptyCellContextOpen] = useState(false);
  const [emptyCellContextAnchor, setEmptyCellContextAnchor] = useState(null);
  const emptyCellIntentRef = useRef(null);

  useEffect(() => {
    emptyCellContextOpenRef.current = emptyCellContextOpen;
  }, [emptyCellContextOpen]);

  const closeEmptyCellContextMenu = useCallback(() => {
    setEmptyCellContextOpen(false);
    setEmptyCellContextAnchor(null);
    emptyCellContextMenuKeyRef.current = null;
    emptyCellIntentRef.current = null;
  }, []);

  const onEmptyCellContextMenu = useCallback(
    (e, payload) => {
      if (!handleCellClick && !pasteScopeBlockAtCell) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      const anchorRect = e.currentTarget?.getBoundingClientRect?.() ?? null;
      if (!anchorRect) {
        return;
      }
      const key = `${payload.row.id}|${toLocalDateKey(payload.day)}`;
      const toggleClose =
        emptyCellContextOpenRef.current &&
        emptyCellContextMenuKeyRef.current === key;
      if (toggleClose) {
        closeEmptyCellContextMenu();
        return;
      }
      emptyCellContextMenuKeyRef.current = key;
      emptyCellIntentRef.current = payload;
      setEmptyCellContextAnchor(anchorRect);
      setEmptyCellContextOpen(true);
    },
    [handleCellClick, pasteScopeBlockAtCell, closeEmptyCellContextMenu]
  );

  const emptyCellContextItems = useMemo(
    () => [
      {
        id: "create-scope",
        label: "Create Scope",
        disabled: !handleCellClick,
        onSelect: () => {
          const p = emptyCellIntentRef.current;
          if (p && handleCellClick) {
            handleCellClick({
              row: p.row,
              rowIndex: p.rowIdx,
              day: p.day,
              dayIndex: p.dayIdx,
            });
          }
        },
      },
      {
        id: "paste-scope",
        label: "Paste Scope",
        disabled: clipboardRevision < 1,
        onSelect: () => {
          const p = emptyCellIntentRef.current;
          if (p && pasteScopeBlockAtCell) {
            pasteScopeBlockAtCell({ row: p.row, day: p.day });
          }
        },
      },
    ],
    [handleCellClick, clipboardRevision, pasteScopeBlockAtCell]
  );

  const jobByProjectId = useMemo(() => {
    const m = new Map();
    for (const j of visibleJobs) {
      m.set(j.id, j);
    }
    return m;
  }, [visibleJobs]);

  const workScopeById = useMemo(() => {
    const m = new Map();
    for (const ws of workScopes) {
      if (ws?.id != null) {
        m.set(ws.id, ws);
      }
    }
    return m;
  }, [workScopes]);

  const todayIndex = useMemo(() => {
    const t = new Date();
    return visibleDays.findIndex(
      (day) =>
        day.getFullYear() === t.getFullYear() &&
        day.getMonth() === t.getMonth() &&
        day.getDate() === t.getDate()
    );
  }, [visibleDays]);

  useEffect(() => {
    const node = scrollContainerRef?.current;
    if (!node) {
      return;
    }
    if (Math.abs(node.scrollLeft - horizontalScrollLeft) > 1) {
      node.scrollLeft = horizontalScrollLeft;
    }
  }, [horizontalScrollLeft, scrollContainerRef]);

  const clearDrag = useCallback(
    (handleEl, pointerId) => {
      if (handleEl && pointerId != null) {
        try {
          handleEl.releasePointerCapture(pointerId);
        } catch {
          /* already released */
        }
      }
      dragRef.current = null;
      handleResizePreview(null);
    },
    [handleResizePreview]
  );

  const onResizePointerMove = useCallback(
    (e) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) {
        return;
      }
      const { newStart, newEnd } = datesFromDragSession(
        d,
        e.clientX,
        cellWidth
      );
      handleResizePreview({
        blockId: d.blockId,
        startDate: newStart,
        endDate: newEnd,
      });
    },
    [cellWidth, handleResizePreview]
  );

  const onResizePointerUp = useCallback(
    (e) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) {
        return;
      }
      const { newStart, newEnd } = datesFromDragSession(
        d,
        e.clientX,
        cellWidth
      );
      handleResizeCommit(d.blockId, newStart, newEnd, d.edge);
      clearDrag(e.currentTarget, e.pointerId);
    },
    [cellWidth, clearDrag, handleResizeCommit]
  );

  const onResizePointerCancel = useCallback(
    (e) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) {
        return;
      }
      clearDrag(e.currentTarget, e.pointerId);
    },
    [clearDrag]
  );

  const beginResize = useCallback(
    (e, blockId, edge) => {
      e.preventDefault();
      e.stopPropagation();
      const block = blocks.find((b) => sameScheduleBlockId(b.id, blockId));
      if (!block) {
        return;
      }
      const { start, end } = getResizeSessionOrigins(block, workScopeAspects);
      const origStart = toLocalCalendarDay(start);
      const origEnd = toLocalCalendarDay(end);
      dragRef.current = {
        pointerId: e.pointerId,
        blockId,
        edge,
        startClientX: e.clientX,
        origStart,
        origEnd,
        visibleDays,
        originStartIndex: findVisibleEdgeIndex(visibleDays, origStart, "start"),
        originEndIndex: findVisibleEdgeIndex(visibleDays, origEnd, "end"),
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [blocks, visibleDays, workScopeAspects]
  );

  const rowCount = scheduleRows.length;
  const rowsForGrid =
    rowCount > 0 ? scheduleRows : [{ id: "schedule-empty-grid-row" }];
  const gridHeight =
    totalGridHeight > 0
      ? totalGridHeight
      : rowsForGrid.length * SCHEDULE_ROW_HEIGHT;

  return (
    <div className={`flex min-h-0 min-w-0 flex-1 flex-col ${BG_SURFACE}`}>
      <div
        ref={scrollContainerRef}
        className="min-h-0 flex-1 overflow-hidden"
        onScroll={onScroll}
      >
        <div style={{ minWidth: timelineWidth }}>
          <div className="relative">
            {todayIndex >= 0 && rowsForGrid.length > 0 ? (
              <div
                className="pointer-events-none absolute left-0 right-0 z-10"
                style={{
                  top: 0,
                  height: gridHeight,
                }}
              >
                <div
                  className="absolute top-0 bottom-0"
                  style={{
                    left: todayIndex * cellWidth + cellWidth / 2,
                    width: 2,
                    backgroundColor: "#EF4444",
                  }}
                />
              </div>
            ) : null}

            {rowsForGrid.map((row, rowIdx) => {
              const isPlaceholder = row.id === "schedule-empty-grid-row";
              const visualRowHeight =
                rowHeightsById?.get(row.id) ?? SCHEDULE_ROW_HEIGHT;
              return (
                <div
                  key={row.id ?? `row-${rowIdx}`}
                  className="flex"
                  style={{
                    height: visualRowHeight,
                    minHeight: visualRowHeight,
                  }}
                >
                  {visibleDays.map((day, dayIdx) => {
                    const dayKey = toLocalDateKey(day);
                    const dayLocked = Boolean(isScheduleDayLocked?.(dayKey));
                    const isWeekend =
                      day.getDay() === 0 || day.getDay() === 6;
                    const job =
                      row.projectId != null
                        ? jobByProjectId.get(row.projectId)
                        : null;
                    const inJobRange = Boolean(
                      job &&
                      isDayWithinJobActiveRange(toLocalDateKey(day), job)
                    );
                    const bgTone = getScheduleGridCellBgClass({
                      row,
                      isWeekend,
                      isPlaceholder,
                      job,
                      inJobRange,
                    });
                    const nextDay =
                      dayIdx < visibleDays.length - 1
                        ? visibleDays[dayIdx + 1]
                        : null;
                    const weekGapBorderAfter =
                      nextDay != null &&
                      isScheduleWeekGapDivider(day, nextDay);
                    const borderTone = getScheduleRowCellBorderClass(row, {
                      omitRight: weekGapBorderAfter,
                    });
                    return (
                      <TimelineGridCell
                        key={`${rowIdx}-${dayIdx}-${dayKey}`}
                        row={row}
                        day={day}
                        rowIdx={rowIdx}
                        dayIdx={dayIdx}
                        cellWidth={cellWidth}
                        hasJob={Boolean(job)}
                        inJobRange={Boolean(inJobRange)}
                        isPlaceholder={isPlaceholder}
                        bgTone={bgTone}
                        borderTone={borderTone}
                        weekGapBorderAfter={weekGapBorderAfter}
                        onCellContextMenu={onEmptyCellContextMenu}
                        dayLocked={dayLocked}
                      />
                    );
                  })}
                </div>
              );
            })}

            {scopeBars.map((scopeBar) => {
              const block = blocks.find((b) =>
                sameScheduleBlockId(b.id, scopeBar.blockId)
              );
              return (
                <ScopeBar
                  key={scopeBar.id}
                  bar={scopeBar}
                  block={block}
                  workScopeById={workScopeById}
                  cellWidth={cellWidth}
                  visibleDays={visibleDays}
                  beginResize={beginResize}
                  onResizePointerMove={onResizePointerMove}
                  onResizePointerUp={onResizePointerUp}
                  onResizePointerCancel={onResizePointerCancel}
                />
              );
            })}
            <CascadeDropDown
              open={emptyCellContextOpen && Boolean(emptyCellContextAnchor)}
              onClose={closeEmptyCellContextMenu}
              anchorRect={emptyCellContextAnchor}
              items={
                handleCellClick || pasteScopeBlockAtCell
                  ? emptyCellContextItems
                  : []
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
